import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAsyncData } from "@/hooks/useAsyncData";
import { listAll, removeItem, updateItem } from "@/lib/adminRepository";
import { ADMIN } from "@/constants/testIds";
import { Plus, Pencil, Trash2, Star, StarOff } from "lucide-react";

// Sort latest published date first (newest → oldest); explicit order breaks ties.
const byDateDesc = (a, b) =>
  String(b.date || "").localeCompare(String(a.date || "")) || (a.order ?? 99) - (b.order ?? 99);

export default function TestimonialsList() {
  const { data: rows, loading, error, reload } = useAsyncData(() => listAll("testimonials"), []);
  const items = [...(rows || [])].sort(byDateDesc);

  const toggleFeatured = async (item) => {
    try {
      await updateItem("testimonials", item.id, { featured: !item.featured });
      reload();
    } catch {
      toast.error("Could not update featured.");
    }
  };

  const del = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete testimonial from ${item.name || item.id}?`)) return;
    try {
      await removeItem("testimonials", item.id);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    await updateItem("testimonials", item.id, { status: next });
    reload();
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow text-bronze mb-1">Content</div>
          <h1 className="font-serif text-3xl text-ink">Testimonials</h1>
          <p className="text-sm text-olive mt-1">Client stories — sorted newest first by date published.</p>
        </div>
        <Link to="/admin/testimonials/new" data-testid={ADMIN.newButton} className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> New testimonial
        </Link>
      </div>

      {loading && <p className="text-olive">Loading…</p>}
      {error && <p className="text-red-700">Failed to load. <button className="underline" onClick={reload}>Retry</button></p>}

      {!loading && !error && (
        <table className="w-full text-sm border border-bronze/20 bg-cream" data-testid={ADMIN.list}>
          <thead className="bg-beige/60 text-left text-[0.65rem] uppercase tracking-wider text-olive">
            <tr>
              <th className="p-3 w-14"></th>
              <th className="p-3">Client</th>
              <th className="p-3">Quote</th>
              <th className="p-3 w-32">Date published</th>
              <th className="p-3 w-24">Visibility</th>
              <th className="p-3 w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-bronze/10 align-middle" data-testid={`admin-row-${item.id}`}>
                <td className="p-2">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-beige border border-bronze/20">
                    {item.image && <img src={item.image} alt="" className="h-full w-full object-cover" />}
                  </div>
                </td>
                <td className="p-3">
                  <div className="font-medium text-ink">{item.name || item.id}</div>
                  <div className="text-xs text-olive">{item.role || "—"}</div>
                </td>
                <td className="p-3 text-ink_soft max-w-md">
                  <div className="line-clamp-2">{item.quote || "—"}</div>
                </td>
                <td className="p-3 text-xs text-ink_soft" data-testid={`testimonial-date-${item.id}`}>{item.date || "—"}</td>
                <td className="p-3">
                  <button
                    onClick={() => togglePublish(item)}
                    data-testid={ADMIN.publishToggle}
                    className={`px-2 py-1 text-[0.6rem] uppercase tracking-wider ${item.status === "published" ? "bg-bronze text-cream" : "bg-taupe/40 text-ink"}`}
                  >
                    {item.status || "draft"}
                  </button>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleFeatured(item)}
                      title={item.featured ? "Featured — click to unfeature" : "Feature in homepage carousel"}
                      data-testid={`testimonial-featured-${item.id}`}
                      className={item.featured ? "text-bronze" : "text-olive/50 hover:text-bronze"}
                    >
                      {item.featured ? <Star size={15} /> : <StarOff size={15} />}
                    </button>
                    <Link to={`/admin/testimonials/${item.id}`} className="text-bronze hover:text-bronze_dark" title="Edit"><Pencil size={15} /></Link>
                    <button onClick={() => del(item)} data-testid={ADMIN.deleteButton} className="text-red-600 hover:text-red-800" title="Delete"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-olive">No testimonials yet — click “New testimonial”.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
